import { Check } from "lucide-react";

import type { QuoteStatusValue } from "@/api/types";

interface Step {
  key: QuoteStatusValue;
  label: string;
}

const PRIMARY_FLOW: Step[] = [
  { key: "draft", label: "Draft" },
  { key: "ready", label: "Ready" },
  { key: "sent", label: "Sent" },
  { key: "accepted", label: "Accepted" },
];

/** Horizontal stepper above the quote header showing where the quote
 * is in its lifecycle. ``declined`` and ``expired`` are terminal
 * sidebars — they don't appear inline with accepted, they replace
 * the right end of the bar. */
export function QuoteStatusBar({ status }: { status: QuoteStatusValue }) {
  const isDeclined = status === "declined";
  const isExpired = status === "expired";

  // Compute current-step index. accepted is a "completed all four"
  // terminal — push currentIdx past the end so every step (including
  // the final ACCEPTED) renders as complete (yellow check). sent is
  // current-with-checkmarks-behind. declined / expired land at the
  // SENT stop with the right end painted red.
  const isAccepted = status === "accepted";
  const currentIdx =
    status === "draft"
      ? 0
      : status === "ready"
        ? 1
        : status === "sent"
          ? 2
          : isAccepted
            ? PRIMARY_FLOW.length // every step complete
            : 2; // declined / expired

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center">
        {PRIMARY_FLOW.map((step, idx) => {
          const isComplete = idx < currentIdx;
          const isCurrent = idx === currentIdx && !isDeclined && !isExpired && !isAccepted;
          const isLastTerminal = idx === PRIMARY_FLOW.length - 1 && (isDeclined || isExpired);

          return (
            <div key={step.key} className="flex flex-1 items-center" style={{ minWidth: 0 }}>
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={[
                    "flex h-7 w-7 items-center justify-center rounded-full border-2 font-mono text-xs font-bold transition",
                    isComplete
                      ? "border-pencil-yellow bg-pencil-yellow text-graphite"
                      : isCurrent
                        ? "border-graphite bg-graphite text-paper"
                        : isLastTerminal
                          ? "border-red-pencil/50 bg-red-pencil/10 text-red-pencil"
                          : "border-rule bg-paper text-graphite-faint",
                  ].join(" ")}
                >
                  {isComplete ? (
                    <Check className="h-3.5 w-3.5" aria-hidden />
                  ) : isLastTerminal ? (
                    "×"
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={[
                    "label-mono whitespace-nowrap",
                    isCurrent
                      ? "font-bold text-graphite"
                      : isComplete
                        ? "text-graphite"
                        : isLastTerminal
                          ? "text-red-pencil"
                          : "text-graphite-faint",
                  ].join(" ")}
                >
                  {isLastTerminal && idx === PRIMARY_FLOW.length - 1
                    ? isDeclined
                      ? "Declined"
                      : "Expired"
                    : step.label}
                </span>
              </div>
              {idx < PRIMARY_FLOW.length - 1 && (
                <div
                  className={[
                    "mx-2 h-0.5 flex-1 transition",
                    idx < currentIdx
                      ? "bg-pencil-yellow"
                      : isLastTerminal && idx === PRIMARY_FLOW.length - 2
                        ? "bg-red-pencil/30"
                        : "bg-rule",
                  ].join(" ")}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
